import React, { useState, useEffect } from 'react';
import {
  getAffiliatePayouts,
  getAffiliateDetail,
  markCommissionsPaid,
  voidCommissions,
} from '../../services/api';

function AffiliatePayouts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedAffiliate, setExpandedAffiliate] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [payoutNotes, setPayoutNotes] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  useEffect(() => {
    fetchPayouts();
  }, []);

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      const result = await getAffiliatePayouts();
      setData(result);
    } catch (err) {
      // Fall back to empty state instead of showing an error
      setData({
        affiliates: [],
        summary: {
          total_pending_cents: 0,
          total_paid_all_time_cents: 0,
          affiliates_with_pending: 0,
          total_affiliates: 0,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExpandAffiliate = async (affiliateId) => {
    if (expandedAffiliate === affiliateId) {
      setExpandedAffiliate(null);
      setCommissions([]);
      setSelectedCommissions([]);
      return;
    }

    setCommissionLoading(true);
    setExpandedAffiliate(affiliateId);
    setSelectedCommissions([]);
    try {
      const result = await getAffiliateDetail(affiliateId);
      setCommissions(result.commissions || []);
    } catch (err) {
      setCommissions([]);
    } finally {
      setCommissionLoading(false);
    }
  };

  const toggleCommission = (id) => {
    setSelectedCommissions((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const selectAllPending = () => {
    const pendingIds = commissions
      .filter((c) => c.status === 'pending' || c.status === 'approved')
      .map((c) => c.id);
    setSelectedCommissions(pendingIds);
  };

  const handleMarkPaid = async () => {
    if (selectedCommissions.length === 0) return;
    setActionMessage('');
    try {
      const result = await markCommissionsPaid(selectedCommissions, payoutNotes);
      setActionMessage(result.message);
      setSelectedCommissions([]);
      setPayoutNotes('');
      // Refresh both data sources
      await fetchPayouts();
      if (expandedAffiliate) {
        const detail = await getAffiliateDetail(expandedAffiliate);
        setCommissions(detail.commissions || []);
      }
    } catch (err) {
      setActionMessage('Failed to mark commissions as paid.');
    }
  };

  const handleVoid = async () => {
    if (selectedCommissions.length === 0) return;
    setActionMessage('');
    try {
      const result = await voidCommissions(selectedCommissions, payoutNotes);
      setActionMessage(result.message);
      setSelectedCommissions([]);
      setPayoutNotes('');
      await fetchPayouts();
      if (expandedAffiliate) {
        const detail = await getAffiliateDetail(expandedAffiliate);
        setCommissions(detail.commissions || []);
      }
    } catch (err) {
      setActionMessage('Failed to void commissions.');
    }
  };

  const formatCents = (cents) => `$${(cents / 100).toFixed(2)}`;

  if (loading) {
    return <div style={{ padding: '20px', color: '#999' }}>Loading affiliate payouts...</div>;
  }

  if (!data) return null;

  return (
    <div style={{ padding: '0' }}>
      <h2 style={{ color: '#fff', fontSize: '18px', marginBottom: '16px' }}>Affiliate Payouts</h2>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Total Pending', value: formatCents(data.summary.total_pending_cents), color: '#fbbf24' },
          { label: 'Affiliates w/ Pending', value: data.summary.affiliates_with_pending, color: '#fbbf24' },
          { label: 'Total Paid All-Time', value: formatCents(data.summary.total_paid_all_time_cents), color: '#10b981' },
          { label: 'Total Affiliates', value: data.summary.total_affiliates, color: '#218aff' },
        ].map((stat, i) => (
          <div key={i} style={{
            padding: '14px',
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '8px',
            textAlign: 'center',
          }}>
            <p style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>{stat.label}</p>
            <p style={{ color: stat.color, fontSize: '22px', fontWeight: '700' }}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Affiliates Table */}
      {data.affiliates.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          border: '1px solid #333',
          borderRadius: '8px',
          backgroundColor: '#1a1a1a',
        }}>
          <p style={{ color: '#999', fontSize: '16px', marginBottom: '8px' }}>No affiliates yet</p>
          <p style={{ color: '#666', fontSize: '13px' }}>
            Affiliate profiles are created automatically when users subscribe to a paid plan.
            Once users start sharing their codes, their referrals and commissions will appear here.
          </p>
        </div>
      ) : (
        <div style={{ border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#111' }}>
                <th style={thStyle}>Username</th>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Pending</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Total Earned</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Paid</th>
                <th style={thStyle}>Method</th>
                <th style={thStyle}>Handle</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Referrals</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.affiliates.map((a) => (
                <React.Fragment key={a.affiliate_id}>
                  <tr style={{ borderBottom: '1px solid #222' }}>
                    <td style={tdStyle}>{a.username}</td>
                    <td style={{ ...tdStyle, color: '#218aff', fontFamily: 'monospace' }}>{a.default_code || '-'}</td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: a.is_active ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                        color: a.is_active ? '#10b981' : '#ef4444',
                      }}>
                        {a.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: a.pending_balance_cents > 0 ? '#fbbf24' : '#666' }}>
                      {formatCents(a.pending_balance_cents)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#ccc' }}>{formatCents(a.total_earned_cents)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#10b981' }}>{formatCents(a.total_paid_cents)}</td>
                    <td style={{ ...tdStyle, color: '#999' }}>{a.payout_method || '-'}</td>
                    <td style={{ ...tdStyle, color: '#999', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.payout_handle || '-'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#ccc' }}>{a.referral_count}</td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => handleExpandAffiliate(a.affiliate_id)}
                        style={{
                          padding: '4px 10px',
                          backgroundColor: expandedAffiliate === a.affiliate_id ? '#333' : 'transparent',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          color: '#ccc',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        {expandedAffiliate === a.affiliate_id ? 'Close' : 'Details'}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Commission Detail */}
                  {expandedAffiliate === a.affiliate_id && (
                    <tr>
                      <td colSpan={10} style={{ padding: '0', backgroundColor: '#0a0a0a' }}>
                        <div style={{ padding: '16px 20px' }}>
                          {commissionLoading ? (
                            <p style={{ color: '#999', fontSize: '13px' }}>Loading commissions...</p>
                          ) : commissions.length === 0 ? (
                            <p style={{ color: '#666', fontSize: '13px' }}>No commissions yet.</p>
                          ) : (
                            <>
                              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
                                <button onClick={selectAllPending} style={actionBtnStyle}>
                                  Select All Pending
                                </button>
                                <input
                                  type="text"
                                  value={payoutNotes}
                                  onChange={(e) => setPayoutNotes(e.target.value)}
                                  placeholder="Payout notes (optional)..."
                                  style={{
                                    flex: 1,
                                    padding: '6px 10px',
                                    backgroundColor: '#111',
                                    border: '1px solid #333',
                                    borderRadius: '4px',
                                    color: '#fff',
                                    fontSize: '12px',
                                  }}
                                />
                                <button
                                  onClick={handleMarkPaid}
                                  disabled={selectedCommissions.length === 0}
                                  style={{
                                    ...actionBtnStyle,
                                    backgroundColor: selectedCommissions.length > 0 ? '#10b981' : '#333',
                                    color: '#fff',
                                    borderColor: selectedCommissions.length > 0 ? '#10b981' : '#444',
                                  }}
                                >
                                  Mark Paid ({selectedCommissions.length})
                                </button>
                                <button
                                  onClick={handleVoid}
                                  disabled={selectedCommissions.length === 0}
                                  style={{
                                    ...actionBtnStyle,
                                    backgroundColor: selectedCommissions.length > 0 ? '#ef4444' : '#333',
                                    color: '#fff',
                                    borderColor: selectedCommissions.length > 0 ? '#ef4444' : '#444',
                                  }}
                                >
                                  Void ({selectedCommissions.length})
                                </button>
                              </div>

                              {actionMessage && (
                                <p style={{ fontSize: '12px', color: '#10b981', marginBottom: '8px' }}>{actionMessage}</p>
                              )}

                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#111' }}>
                                    <th style={{ ...thStyle, width: '30px' }}></th>
                                    <th style={thStyle}>Date</th>
                                    <th style={thStyle}>User</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Payment</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>Rate</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Commission</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>Year</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                                    <th style={thStyle}>Paid At</th>
                                    <th style={thStyle}>Paid By</th>
                                    <th style={thStyle}>Method</th>
                                    <th style={thStyle}>Handle</th>
                                    <th style={thStyle}>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {commissions.map((c) => (
                                    <tr key={c.id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                                      <td style={tdStyle}>
                                        {(c.status === 'pending' || c.status === 'approved') && (
                                          <input
                                            type="checkbox"
                                            checked={selectedCommissions.includes(c.id)}
                                            onChange={() => toggleCommission(c.id)}
                                            style={{ cursor: 'pointer' }}
                                          />
                                        )}
                                      </td>
                                      <td style={tdStyle}>{new Date(c.created_at).toLocaleDateString()}</td>
                                      <td style={tdStyle}>{c.referred_username}</td>
                                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCents(c.payment_amount_cents)}</td>
                                      <td style={{ ...tdStyle, textAlign: 'center', color: '#999' }}>
                                        {(c.commission_rate * 100).toFixed(0)}%
                                      </td>
                                      <td style={{ ...tdStyle, textAlign: 'right', color: '#10b981', fontWeight: '600' }}>
                                        {formatCents(c.commission_amount_cents)}
                                      </td>
                                      <td style={{ ...tdStyle, textAlign: 'center', color: '#999' }}>{c.referral_year}</td>
                                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                                        <span style={{
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          fontSize: '10px',
                                          fontWeight: '600',
                                          backgroundColor:
                                            c.status === 'paid' ? 'rgba(16, 185, 129, 0.15)' :
                                            c.status === 'pending' ? 'rgba(251, 191, 36, 0.15)' :
                                            c.status === 'voided' ? 'rgba(239, 68, 68, 0.15)' :
                                            'rgba(33, 138, 255, 0.15)',
                                          color:
                                            c.status === 'paid' ? '#10b981' :
                                            c.status === 'pending' ? '#fbbf24' :
                                            c.status === 'voided' ? '#ef4444' :
                                            '#218aff',
                                        }}>
                                          {c.status}
                                        </span>
                                      </td>
                                      <td style={{ ...tdStyle, color: '#666' }}>
                                        {c.paid_at ? new Date(c.paid_at).toLocaleDateString() : '-'}
                                      </td>
                                      <td style={{ ...tdStyle, color: '#999' }}>{c.paid_by_username || '-'}</td>
                                      <td style={{ ...tdStyle, color: '#999' }}>{c.paid_method || '-'}</td>
                                      <td style={{ ...tdStyle, color: '#999', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {c.paid_handle || '-'}
                                      </td>
                                      <td style={{ ...tdStyle, color: '#999', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.payout_notes || ''}>
                                        {c.payout_notes || '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '8px 12px',
  textAlign: 'left',
  color: '#999',
  borderBottom: '1px solid #333',
  fontWeight: '600',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '8px 12px',
  color: '#ccc',
  whiteSpace: 'nowrap',
};

const actionBtnStyle = {
  padding: '6px 12px',
  backgroundColor: 'transparent',
  border: '1px solid #444',
  borderRadius: '4px',
  color: '#ccc',
  fontSize: '12px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export default AffiliatePayouts;
